import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, CheckCircle2, ChevronDown, CircleHelp, Clock3, Globe2,
  Inbox, LayoutGrid, LifeBuoy, MessageSquareText, Plus, Search, Sparkles,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useSelfServiceCreation } from "../features/capabilities/SelfServiceCreation";
import SiteGallery, { canonicalSites } from "../components/SiteGallery";
import SessionUnavailable from "../components/SessionUnavailable";
import { siteHost } from "../lib/domains";
import { useLiveSites } from "../lib/useLiveSites";

function PageHeader({ eyebrow, title, description, action }: {
  eyebrow?: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="studio-page-header">
      <div>
        {eyebrow && <div className="studio-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

export function MySites() {
  const showCreation = useSelfServiceCreation();
  const auth = useAuth();
  const { sites, setSites, error, refresh } = useLiveSites();
  const [showArchived, setShowArchived] = useState(false);
  const ownedSites = auth.status === "authed" && sites
    ? canonicalSites(sites.filter(site => site.ownerUserId === auth.me.userId)) : [];
  const archivedSites = ownedSites.filter(site => site.archivedAt);
  const workspaceSites = ownedSites.filter(site => !site.archivedAt);
  const visibleSites = showArchived ? archivedSites : workspaceSites;
  if (auth.status === "error") return <SessionUnavailable retry={auth.retry} />;
  if (auth.status !== "authed") return null;
  return (
    <div className="studio-page">
      <PageHeader
        title="My sites"
        description="One place for each website. Edits update the same site; earlier versions stay in its history."
        action={showCreation && <Link to="/start" className="btn primary"><Plus size={15} /> New website</Link>}
      />
      {error && <div className="notice error" role="alert">Your websites could not load. <button className="btn" onClick={() => void refresh()}>Try again</button></div>}
      {sites === null && !error && <div className="studio-loading-card">Loading your sites…</div>}
      {sites !== null && ownedSites.length === 0 && (
        <div className="studio-empty">
          <div className="empty-icon"><LayoutGrid size={24} /></div>
          <h2>{showCreation ? "Your first site starts here" : "Your first site will appear here"}</h2>
          <p>{showCreation ? "Bring in an existing business or describe a new one from scratch." : "The ProtoPal team prepares your website. You can edit and publish it here once it is ready."}</p>
          {showCreation && <div className="row">
            <Link to="/start/business" className="btn primary">Find my business</Link>
            <Link to="/start/scratch" className="btn">Build from scratch</Link>
          </div>}
        </div>
      )}
      {ownedSites.length > 0 && <>
        <div className="row" role="group" aria-label="Sites to show" style={{ flexWrap: "wrap", gap: 8 }}>
          <button className={`btn${!showArchived ? " primary" : ""}`} aria-pressed={!showArchived} onClick={() => setShowArchived(false)}>Workspace ({workspaceSites.length})</button>
          <button className={`btn${showArchived ? " primary" : ""}`} aria-pressed={showArchived} onClick={() => setShowArchived(true)}>Archived ({archivedSites.length})</button>
        </div>
        <p className="muted">Archiving only hides a site from this workspace. Its live website and version history stay available.</p>
        {visibleSites.length ? <SiteGallery sites={visibleSites} onRefresh={() => void refresh()}
          onChanged={updated => setSites(current => current?.map(site => site.id === updated.id ? updated : site) ?? [])} />
          : <div className="studio-empty"><h2>{showArchived ? "No archived sites" : "Your sites are archived"}</h2><p>{showArchived ? "Archived sites will appear here, ready to restore." : "Open Archived to restore a site to this workspace."}</p></div>}
      </>}
    </div>
  );
}

export function Domains() {
  const auth = useAuth();
  if (auth.status === "error") return <SessionUnavailable retry={auth.retry} />;
  if (auth.status !== "authed") return null;
  return (
    <div className="studio-page">
      <PageHeader eyebrow="Publishing" title="Web address" description="See where each ProtoPal website lives on the web." />
      <div className="settings-card">
        <div className="settings-card-head"><div><h2>Included ProtoPal addresses</h2><p>Every published site includes secure hosting and SSL.</p></div><Globe2 size={20} /></div>
        <div className="domain-list">
          {auth.me.sites.length === 0 && <div className="truthful-empty">Create a website before choosing its web address.</div>}
          {auth.me.sites.map((site) => (
            <div className="domain-row" key={site.id}>
              <span className={`status-dot ${site.status}`} />
              <div><strong>{siteHost(site.subdomain)}</strong><small>{site.status === "active" ? "Published and available" : "Reserved for this site"}</small></div>
              <Link to={`/sites/${site.id}`} className="btn">Manage</Link>
            </div>
          ))}
        </div>
      </div>
      <div className="settings-card coming-card">
        <div className="settings-card-head"><div><span className="coming-pill">Coming soon</span><h2>Connect a custom domain</h2><p>Use an address you already own. We will guide DNS setup and keep SSL renewed.</p></div><Clock3 size={20} /></div>
        <button className="btn" disabled>Connect domain</button>
      </div>
    </div>
  );
}

export function Leads() {
  return (
    <div className="studio-page">
      <PageHeader eyebrow="Customer inbox" title="Leads" description="A home for inquiries collected by your ProtoPal websites." />
      <div className="studio-empty large">
        <div className="empty-icon"><Inbox size={25} /></div>
        <span className="coming-pill">Coming soon</span>
        <h2>Your leads will land here</h2>
        <p>Lead capture is being prepared. No messages or customer data are being claimed or fabricated in the meantime.</p>
        <Link to="/help" className="btn">Learn what&apos;s planned</Link>
      </div>
    </div>
  );
}

type HandbookEntry = { id: string; tag: string; title: string; summary: string; body: ReactNode };

const HANDBOOK: HandbookEntry[] = [
  {
    id: "create",
    tag: "Getting started",
    title: "Create your first website",
    summary: "Two ways to start — pull an existing business from Google, or describe a new one.",
    body: (
      <>
        <p>From your dashboard, choose <strong>Find my business</strong> or <strong>Start from scratch</strong>.</p>
        <ul>
          <li><strong>Find my business</strong> looks you up on Google and fills in your name, address, hours, and photos automatically — less typing.</li>
          <li><strong>Start from scratch</strong> asks a few quick questions about your business and builds from your answers.</li>
        </ul>
        <p>Either way, ProtoPal creates your pages, copy, and visual design in the background. Follow the build's progress, then review your preview before publishing.</p>
      </>
    ),
  },
  {
    id: "edit",
    tag: "Editing",
    title: "Edit your site by chat or by text",
    summary: "Describe a change in the editor, or text it to your site from your phone.",
    body: (
      <>
        <p>Open a site and choose <strong>Edit website</strong> to make changes by chat. Describe what you want — “make the hero photo bigger”, “change the headline to…” — and ProtoPal applies it. Select a specific element first to change just that one, or ask generally and ProtoPal works out what you mean.</p>
        <p>You can also <strong>text your site a change</strong>. Send a message to your ProtoPal number and the update comes back when it’s ready, with a preview link. Small wording, colour, and spacing tweaks are quick; a full redesign takes a little longer.</p>
      </>
    ),
  },
  {
    id: "publish",
    tag: "Publishing",
    title: "Review and publish a draft",
    summary: "Every build is a private preview until you choose to publish it.",
    body: (
      <>
        <p>Every new build is a <strong>private draft</strong> only you can see. Open the preview, make any changes, and choose <strong>Publish live</strong> when it feels right.</p>
        <p>Your published site stays up the whole time you’re working on a new version — nothing goes down until you publish the new one.</p>
      </>
    ),
  },
  {
    id: "status",
    tag: "Status",
    title: "What each build status means",
    summary: "Building, Draft, Live, and “needs attention”, explained.",
    body: (
      <ul>
        <li><strong>Building</strong> — ProtoPal is creating and checking your draft. Open the build to follow its progress.</li>
        <li><strong>Draft</strong> — a private preview is ready but not published yet.</li>
        <li><strong>Live</strong> — published and available at your web address.</li>
        <li><strong>Needs attention</strong> — a build didn’t finish. Your site and details are safe and nothing is charged twice; just start the build again.</li>
      </ul>
    ),
  },
  {
    id: "plans",
    tag: "Plans & billing",
    title: "Plans, edits, and redesigns",
    summary: "What your plan includes and how to upgrade.",
    body: (
      <>
        <p>Each plan includes a set number of <strong>sites</strong>, <strong>text edits</strong>, and <strong>redesigns</strong> per month. A text edit is a small wording or style change; a redesign rebuilds a site from scratch.</p>
        <p>See your current usage and upgrade any time under <strong>Account → Plan</strong>. We’ll always show you the cost before anything is charged.</p>
      </>
    ),
  },
  {
    id: "address",
    tag: "Publishing",
    title: "Your web address and integrations",
    summary: "Where your site lives, and how to add booking, social, and ordering links.",
    body: (
      <>
        <p>Every published site includes hosting and a secure <strong>protopal.ai</strong> web address with SSL. You can see each site’s address under <strong>Web address</strong>.</p>
        <p>Add booking, social, ordering, and marketplace links from a site’s <strong>Plugins &amp; integrations</strong> page.</p>
      </>
    ),
  },
];

export function HelpCenter() {
  const showCreation = useSelfServiceCreation();
  const handbook = showCreation ? HANDBOOK : HANDBOOK.map((entry) => entry.id === "create" ? {
    ...entry, title: "Getting your website set up",
    summary: "The ProtoPal team prepares your website and adds it to your account.",
    body: <p>Contact the ProtoPal team to arrange your website. Once it appears in your account, you can edit it, review changes, and publish.</p>,
  } : entry);
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? handbook.filter((a) => `${a.title} ${a.summary} ${a.tag}`.toLowerCase().includes(needle))
    : handbook;
  return (
    <div className="studio-page">
      <PageHeader eyebrow="Resources" title="Handbook" description="Straightforward guidance for building, editing, and publishing your site." />
      <div className="help-search">
        <Search size={18} />
        <input
          type="search"
          placeholder="Search the handbook…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="handbook">
        {shown.map((a) => (
          <details key={a.id} className="handbook-item">
            <summary>
              <span className="handbook-item-head">
                <span className="handbook-tag">{a.tag}</span>
                <span className="handbook-title">{a.title}</span>
                <span className="handbook-summary">{a.summary}</span>
              </span>
              <ChevronDown className="handbook-chevron" size={18} />
            </summary>
            <div className="handbook-body">{a.body}</div>
          </details>
        ))}
        {shown.length === 0 && (
          <div className="truthful-empty">No handbook entries match “{query.trim()}”.</div>
        )}
      </div>
      <div className="support-band">
        <div className="empty-icon"><LifeBuoy size={21} /></div>
        <div><h2>Still need a hand?</h2><p>Talk to the ProtoPal team about your website or account.</p></div>
        <a className="btn primary" href="mailto:dev@prototypical.ai">Contact support</a>
      </div>
    </div>
  );
}

export function WhatsNew() {
  const updates = [
    { date: "July 2026", title: "A new ProtoPal workspace", copy: "A clearer home for your sites, publishing controls, integrations, and account settings.", icon: Sparkles },
    { date: "July 2026", title: "Secure prompt handoff", copy: "Ideas entered on ProtoPal now follow you safely through sign-in and open in Build from Scratch.", icon: CheckCircle2 },
    { date: "June 2026", title: "Faster website changes", copy: "Send a focused change request from the site workspace without taking your live site down.", icon: MessageSquareText },
  ];
  return (
    <div className="studio-page narrow-page">
      <PageHeader eyebrow="Product updates" title="What&apos;s new" description="The latest improvements to ProtoPal." />
      <div className="updates-list">
        {updates.map(({ date, title, copy, icon: Icon }) => <article key={title}><div className="update-icon"><Icon size={18} /></div><div><time>{date}</time><h2>{title}</h2><p>{copy}</p></div></article>)}
      </div>
    </div>
  );
}
